import assert from 'assert';

import {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {ExperimentalInputEvaluatedValues} from 'procedures/planning/ProcedurePlanTypes';

import {Condition} from '../process-development/useProcessDevelopmentStudyState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

export function areProcedureRunInputsEvaluatedValuesValid(
  evaluatedValues: ExperimentalInputEvaluatedValues
): boolean {
  return Object.values(evaluatedValues).every((methodData) => methodData.blockingValueErrorCount === 0);
}

/* True if conditions are valid for creation, false otherwise. */
export function validateConditions(conditions: Array<Condition>): boolean {
  const conditionsValid = !conditions.some(
    ({validationState}) =>
      validationState?.isNameLengthOverLimit ||
      validationState?.isDuplicateName ||
      validationState?.isDescLengthOverLimit
  );
  return conditionsValid;
}

export function isNumConditionReplicatesValid(methodExecutionLimitState: MethodExecutionLimitState): boolean {
  return !methodExecutionLimitState.isOverLimit;
}

const CONDITION_SETTINGS_CATEGORY = 'Condition settings';

/**
 * Collects all validation errors for a procedure run plan into ErrorBadgeError objects
 * suitable for display in the header error badge.
 *
 * Errors are categorized by method name or "Condition settings" so the badge popover
 * groups them under the appropriate headings.
 */
export function collectPlanErrors({
  evaluatedValues,
  methodExecutionLimitState,
  conditions,
  methodIdToName,
  conditionsText,
}: {
  evaluatedValues: ExperimentalInputEvaluatedValues;
  methodExecutionLimitState: MethodExecutionLimitState;
  conditions: Array<Condition>;
  methodIdToName: Record<string, string>;
  conditionsText: string;
}): Array<ErrorBadgeError> {
  const errors: Array<ErrorBadgeError> = [];

  // Blocking value errors in evaluated values, grouped by method name.
  for (const [methodId, values] of Object.entries(evaluatedValues)) {
    if (values.blockingValueErrorCount > 0) {
      const category = methodIdToName[methodId] ?? '';
      assert(category !== '', `Expected method ${methodId} to have a name`);
      errors.push({
        message: `Some ${conditionsText} have invalid values specified`,
        category,
        count: values.blockingValueErrorCount,
      });
    }
  }

  // Condition-replicate limit exceeded
  if (methodExecutionLimitState.isOverLimit) {
    errors.push({
      message: `Cannot exceed ${methodExecutionLimitState.replicateLimit} total replicates, reduce number of ${conditionsText} or replicates`,
      category: CONDITION_SETTINGS_CATEGORY,
    });
  }

  // Condition name/description validation errors
  for (const condition of conditions) {
    if (condition.validationState?.isDuplicateName) {
      errors.push({message: `Duplicate name: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isNameLengthOverLimit) {
      errors.push({message: `Name is too long: ${condition.name}`, category: CONDITION_SETTINGS_CATEGORY});
    }
    if (condition.validationState?.isDescLengthOverLimit) {
      errors.push({
        message: `Condition description is too long: ${condition.name}`,
        category: CONDITION_SETTINGS_CATEGORY,
      });
    }
  }

  return errors;
}
